import { z } from "zod";

import { assetSchema } from "./asset";
import { enterpriseSchema } from "./enterprise";
import { folderSchema } from "./folder";
import { teamSchema } from "./team";
import { userSchema } from "./user";
import { workflowSchema } from "./workflow";

export const fileCoordSchema = z.object({
  x: z.coerce.number().optional(),
  y: z.coerce.number().optional(),
  width: z.coerce.number(),
  height: z.coerce.number(),
  filename: z.string().optional(),
  source: z.string(),
  provider: z.string().optional(),
  provider_task_id: z.string().optional(),
  provider_attachment_id: z.string().optional(),
  provider_permalink: z.string().optional(),
  provider_source_url: z.string().optional(),
  wrike_task_id: z.string().optional(),
  wrike_attachment_id: z.string().optional(),
  asana_task_id: z.string().optional(),
  asana_attachment_id: z.string().optional(),
});

export enum RevisionBoardErrorReason {
  FILE_TOO_LARGE = "file_too_large",
  DIMENSIONS_TOO_LARGE = "dimensions_too_large",
  DIMENSIONS_TOO_SMALL = "dimensions_too_small",
  VIDEO_TOO_LONG = "video_too_long",
  VIDEO_TOO_LARGE = "video_too_large",
  TOO_MANY_FILES = "too_many_files",
  TOO_MANY_PAGES = "too_many_pages",
  DOC_CONVERSION_TIMEOUT = "doc_conversion_timeout",
  DOC_CONVERSION_ERROR = "doc_conversion_error",
  DOWNLOAD_FAILED = "download_failed",
  INVALID_VIDEO = "invalid_video",
  HLS_TIMEOUT = "hls_timeout",
  PROCESSING_FAILED = "processing_failed",
  VIDEO_ANALYSIS_FAILED = "video_analysis_failed",
}

export type FileCoord = z.infer<typeof fileCoordSchema>;

export enum TicketWorkflow {
  EVENT_DESIGN = "event_design",
  SOCIAL_MEDIA_POST = "social_media_post",
  PRODUCTION_FLOW = "production_design_flow",
}

export enum TicketCommentBoard {
  BRIEF = "briefBoard",
  MOOD_BOARD = "moodBoard",
  REVISION = "revisionBoard",
}

export enum TicketStatus {
  WIP = "wip",
  NEEDS_REVIEW = "needs_review",
  APPROVED = "approved",
  COMPLETE = "complete",
  INCOMPLETE = "incomplete",
}

export enum TicketEmailTemplateAlias {
  TICKET_REVIEW = "ticket-generic",
  TICKET_APPROVED = "ticket-generic",
  TICKET_ADD_REVIEWER = "ticket-generic",
  TICKET_NEW_MESSAGE = "ticket-generic",
  TICKET_REPLY_MESSAGE = "ticket-generic",
  TICKET_ADD_REVISION_BOARD = "ticket-generic",
  TICKET_ADD_CREATIVES = "ticket-generic",
  TICKET_ASSIGN_CREATIVES = "ticket-generic",
  TICKET_MENTION = "ticket-mention",
}

export enum TicketAIStatus {
  CREATED = "created",
  PREPROCESSED = "preprocessed",
  FAILPROCESSED = "failprocessed",
  AIREVIEWED = "aireviewed",
}

export const briefHistorySchema = z.object({
  user: z.union([z.string(), userSchema.partial()]),
  message: z.string(),
  createdAt: z.coerce.date().optional(),
  description: z.string().optional(),
});

export type BriefHistory = z.infer<typeof briefHistorySchema>;

export enum TicketCommentDisposition {
  DEFAULT = "default",
  RESOLVED = "resolved",
  DISMISSED = "dismissed",
}

export enum ComplianceVerdict {
  APPROVED = "approved",
  DERIVATIVE = "derivative",
  NON_COMPLIANT = "non_compliant",
  UNSURE = "unsure",
}

export const complianceVerdictMetaSchema = z.object({
  verdict: z.nativeEnum(ComplianceVerdict),
  claimText: z.string().max(2000).optional(),
  closestApprovedText: z.string().max(2000).optional(),
  rationale: z.string().max(2000).optional(),
  corpusVersion: z.string().optional(),
  marketSource: z.string().optional(),
  marketConfidence: z.enum(["high", "medium", "low"]).optional(),
});

export type ComplianceVerdictMeta = z.infer<typeof complianceVerdictMetaSchema>;

export enum TicketMessageRating {
  THUMBS_UP = "u",
  THUMBS_DOWN = "d",
}

export const ticketMessageVoteSchema = z.object({
  user: z.union([z.string(), userSchema.partial()]),
  vote: z.nativeEnum(TicketMessageRating),
  createdAt: z.coerce.date().optional(),
});

// why: rembrandt's KDB extraction stores every unknown location field as an
// explicit null, and z.coerce.number() would read that null as 0 (failing
// min(1)). Null means "not known" here, so it parses as an omitted field.
//
// Behavior impact on comments stored before this: every field whose stored
// null could not coerce into range threw — `pageNumber` and `cellAnchor.row`
// (null coerces to 0, below `min(1)`), plus `bbox`, `cellAnchor` itself,
// `cellAnchor.range` and `cellAnchor.sheetName`, because `.optional()`
// accepts `undefined` but not `null` (the Danone crash this fixes). A null
// `rel_x` / `rel_y` / `cellAnchor.sheetIndex` coerced to 0 — inside their
// allowed range — so those comments highlighted sheet 0 / the top-left pin.
// They now read as unset and render unanchored instead, which is the
// intended outcome: null is unknown, 0 was a plausible wrong anchor.
// A real stored 0 sheetIndex is still kept (see the `sheet_cell_anchor` case
// in __tests__/fixtures/kdbRuleStoredLocations.json).
//
// Every `citationCellAnchorSchema` member below is pinned ONE PER FIELD in
// __tests__/ticket.test.ts ("citationCellAnchorSchema null-as-unset members"),
// not only through the shared fixture: that fixture never stores a null
// `sheetName` (extraction always resolves a sheet name), so reverting just
// that member to `z.string().optional()` left the whole suite green.
const nullAsUnset = <T extends z.ZodTypeAny>(schema: T) =>
  z
    .preprocess((v) => (v === null ? undefined : v), schema.optional())
    .optional();

// A bbox is an ALL-OR-NOTHING pin, so it needs its own null handling instead of
// `nullAsUnset` on the object alone: with bare `z.coerce.number()` members a
// stored `{x: 0.11, y: null, w: 0.72, h: 0.026}` read `y` as 0 and pinned the
// box to the top edge of the page rather than the cited line — exactly the
// "plausible wrong anchor" the note above exists to remove, and inconsistent
// with `citationCellAnchorSchema`, where the same stored null reads as unset.
// Wrapping each member in `nullAsUnset` is NOT the fix: that types x/y/w/h as
// `number | undefined`, and half a box is not renderable (the carrie pin
// renderers take four required numbers). A null member therefore drops the
// whole bbox to unset, identical to a stored `bbox: null` and symmetric with
// `_usable_bbox` in rembrandt's services/review/knowledge_rule_search.py.
// A member that is MISSING still fails the parse — extraction never emits that
// shape, and silently accepting a 3-sided box would hide a producer bug.
const CITATION_BBOX_MEMBERS = ["x", "y", "w", "h"] as const;
const citationBboxSchema = z.object({
  x: z.coerce.number(),
  y: z.coerce.number(),
  w: z.coerce.number(),
  h: z.coerce.number(),
});
const bboxWithNullMemberAsUnset = z
  .preprocess((v) => {
    if (v === null) return undefined;
    if (typeof v !== "object") return v;
    const members = v as Record<string, unknown>;
    return CITATION_BBOX_MEMBERS.some((m) => members[m] === null)
      ? undefined
      : v;
  }, citationBboxSchema.optional())
  .optional();

// Cited location inside a spreadsheet source. Laxer than the comment-level
// cellAnchorSchema: a citation may only know the sheet (reference resolved
// from a rendered sheet page) or sheet + row (spreadsheet tool lookups), so
// every field is optional.
export const citationCellAnchorSchema = z.object({
  sheetIndex: nullAsUnset(z.coerce.number().int().nonnegative()),
  sheetName: nullAsUnset(z.string()),
  range: nullAsUnset(z.string()), // A1-style cell or range, e.g. "B7" / "A5:C7"
  // 1-based row when no column is known. `.min(1)`, NOT `.positive()`:
  // positive() emits a boolean `exclusiveMinimum` in the generated OpenAPI
  // doc, which jest-openapi's draft-07 spec validation rejects — that fails
  // every christo handler test at startup.
  row: nullAsUnset(z.coerce.number().int().min(1)),
});

export type CitationCellAnchor = z.infer<typeof citationCellAnchorSchema>;

// Cited location inside a knowledge source (pin coordinates). Lives here — the
// lowest module both `commons/knowledge.ts` (rule storage) and the comment
// citation below share — so both reuse one definition instead of drifting.
// Coordinates are 0-1 normalized and denormalized at extraction time; all
// fields optional so a quote-only citation (no anchorable location) is valid.
export const knowledgeCitationLocationSchema = z.object({
  pageNumber: nullAsUnset(z.coerce.number().int().min(1)),
  rel_x: nullAsUnset(z.coerce.number().min(0).max(1)),
  rel_y: nullAsUnset(z.coerce.number().min(0).max(1)),
  bbox: bboxWithNullMemberAsUnset,
  cellAnchor: nullAsUnset(citationCellAnchorSchema),
});
export type KnowledgeCitationLocation = z.infer<
  typeof knowledgeCitationLocationSchema
>;

export const fileCitationSchema = z.object({
  url: z.string().url(), // URL of the file
  pageNumber: z.coerce.number().optional(), // Page number for document types
  originalURL: z.union([z.string().url(), z.literal("")]).optional(),
  rel_x: z.coerce.number().optional(), // Horizontal position on the cited page (0-1)
  rel_y: z.coerce.number().optional(), // Vertical position on the cited page (0-1)
  videoStart: z.coerce.number().optional(), // Seconds into the cited video source
  // Excerpt shown WITH the cited page — the retrieved guideline passage on a
  // compliance-review KB citation (`_ranked_doc_to_webpage_citation` in
  // rembrandt's services/review/video_postprocess.py emits `{url, text}`), and
  // christo persists both on ONE citation subdocument (`url` and `text` are
  // sibling paths in CitationCommonSchema, services/christo/lib/models/ticket.ts).
  //
  // It has to live HERE, not only on `webpageCitationSchema`:
  // `ticketMessageCitationSchema` is a first-match-wins `z.union` and this arm
  // comes first, so EVERY url-bearing citation is parsed by this schema and the
  // webpage arm is never reached. Without `text` zod silently stripped the
  // excerpt on read (carrie's `getTicketCommentsResponseSchema.parse` in
  // frontends/carrie/src/services/projects.ts) and on write
  // (`createTicketCommentBodySchema`), so the comment source dialog — which
  // renders `citation.text` beside `citation.url`, see the `citation.text ?`
  // branch in comment-drawer/Comment.tsx — showed a bare link with no guideline
  // text. Optional/additive: a plain document citation still carries none.
  text: z.string().optional(),
  // Location in a cited spreadsheet. `nullAsUnset`, not `.optional()`: the
  // christo citation subdocument declares `cellAnchor` as a single nested
  // schema (services/christo/lib/models/ticket.ts), and mongoose hydrates an
  // unset single-nested path back as an explicit `null`, so every non-
  // spreadsheet citation reads as `cellAnchor: null`. `.optional()` takes
  // `undefined` but not `null`, which is the same hard parse error this file
  // already removed at the member level — a real anchor still parses, and an
  // out-of-range `row` is still rejected.
  //
  // Its members stay null-tolerant one level down, pinned through THIS schema
  // (the one carrie parses a file citation with) by "reads a null cellAnchor
  // member as unset on a file citation" in __tests__/ticket.test.ts: that case
  // also parses a lone `{ sheetIndex: 1, sheetName: null }`, with no sibling
  // null to absorb the failure, so reverting `sheetName` on its own to
  // `z.string().optional()` goes red here too, not only in the member describe.
  cellAnchor: nullAsUnset(citationCellAnchorSchema),
});

export const knowledgeDataRecordCitationSchema = z.object({
  knowledgeDataRecordId: z.string(),
});

// MIRROR CONTRACT: this schema is NOT the only boundary a ticket-comment rule
// citation crosses. Christo persists ticket comments through mongoose, which
// STRIPS undeclared paths on both hydrate and save, and every ticket write
// re-casts the whole comments array (`TicketModel.findByIdAndUpdate(ticket._id,
// ticket, options)` in services/christo/lib/services/ticket.ts). So a field
// added here that is not also declared on `CitationCommonSchema` in
// services/christo/lib/models/ticket.ts is deleted the first time a ticket
// round-trips — silently, because every read of it downstream is optional-
// chained. `statement`, `sourceName`, `brand` and the NESTED `location` below
// are declared on both sides for exactly that reason, and the nesting is not a
// detail you can skip: mongoose strips per level, so `location` needs its own
// sub-schema declaring `pageNumber`/`rel_x`/`rel_y`/`bbox`/`cellAnchor`, and
// the christo model's TOP-LEVEL `cellAnchor` does NOT cover
// `location.cellAnchor` — they are different paths. Pinned by "defines
// statement/sourceName/brand and a nested location on comment citations" and
// the write round-trip beside it ("keeps statement/sourceName/brand and the
// nested location when a rule citation is written") in
// services/christo/lib/models/__tests__/ticket.test.ts. That second test
// builds the citation through `new TicketModel(...)` — the cast a
// create()/save() runs, which is where an undeclared path is dropped; a
// `hydrate` would pass with the field missing — and asserts the whole citation
// by equality, so a dropped path goes red. Add a field here and you owe that
// file the same path.
//
// A review/chat comment citing a synthesized Knowledge Database rule (the
// PR #4382 `knowledgerules`). Kept strict (ruleId required) and SEPARATE from
// the legacy knowledgeDataRecord kind above so nothing there is loosened.
// Carries the granularity Citation Correctness is scored on, per comment:
//  - ruleId:     which rule was cited (also drives usage.reviewCount)
//  - sourceId:   the knowledgesources doc the rule's evidence lives in
//  - quote:      verbatim source evidence -> "does the source support the comment?"
//  - similarity / confidence: relevance + trust signals for scoring/triage
//  - location: pin coordinates (pageNumber + tight bbox + rel_x/rel_y) so a
//    citation chip can pin the exact source line, not just open the page.
export const knowledgeRuleCommentCitationSchema = z.object({
  ruleId: z.string(),
  sourceId: z.string().optional(),
  // The source document's display name, so a chip can name WHICH source backs
  // the cite at a glance (correlates cite -> source without a click). Optional/
  // additive — populated at citation-build time from the hydrated hit.
  sourceName: z.string().optional(),
  // The rule's plain-English statement, so the chip reads as the CLAIM
  // ("Wake up to Flavour-filled fibre") instead of a raw source-cell fragment.
  // Optional/additive — citations written before this fall back to `quote`.
  statement: z.string().optional(),
  quote: z.string().optional(),
  // The rule's own brand scope, at the granularity the workbook recorded it
  // ("Activia" or "Activia Kefir"). The same claim wording is registered for
  // several brands with different verdicts, so without this the reader cannot
  // tell whose rule a chip belongs to and identical-looking chips collapse
  // into one. Optional/additive — citations written before this carry none.
  brand: z.string().optional(),
  // The rule's verdict (claim.polarity: approved / prohibited / pending /
  // conditional ...). With `brand` it tells identical wordings apart, which is
  // what the inline linker needs to give each phrase its own row. Optional/
  // additive -- citations written before this carry none.
  polarity: z.string().optional(),
  // True when the answer did NOT cite this rule: warhol fills the panel to its
  // cap with the registry's nearest rows (`knowledge_citations.py`), and the
  // reader must not take one for a citation. Optional/additive -- absent means
  // cited, so every citation written before this reads as it always did.
  related: z.boolean().optional(),
  // Pin coordinates for the cited source line (pageNumber + tight bbox +
  // rel_x/rel_y). Optional/additive — citations written before this, or with an
  // unanchorable quote, carry none and the chip stays quote-only.
  location: nullAsUnset(knowledgeCitationLocationSchema),
  // Trust + relevance, read like the pin above: a stored null is UNKNOWN, not
  // a real 0. Bare `z.coerce.number().min(0).max(1)` does not reject one —
  // `Number(null) === 0` lands INSIDE `min(0)` — so it parsed as a genuine 0
  // and the chip read "0% confidence" for a rule that never recorded it. The
  // reader forwards the value raw (`rule.get("confidence")` in rembrandt's
  // services/review/knowledge_rule_search.py), and `.get()` yields None for any
  // rule document written without the key — pinned producer-side by
  // test_rule_to_hit_forwards_an_absent_confidence_as_unknown_not_zero in
  // services/rembrandt/tests/test_knowledge_rule_search.py. A real 0 still
  // parses as 0 — see the `scoreCases` in
  // __tests__/fixtures/kdbRuleStoredLocations.json.
  similarity: nullAsUnset(z.coerce.number().min(0).max(1)),
  confidence: nullAsUnset(z.coerce.number().min(0).max(1)),
  // The rule's tags a rejection can be scoped by -- the reject prompt's choices
  // when a finding names no brand. Only the four scope dimensions; `riskLevel`
  // grades a rule, it does not say who the rule applies to. Optional/additive.
  tags: z
    .object({
      markets: z.array(z.string()).optional(),
      productForms: z.array(z.string()).optional(),
      claimTypes: z.array(z.string()).optional(),
      regulations: z.array(z.string()).optional(),
    })
    .optional(),
});

export type KnowledgeRuleCommentCitation = z.infer<
  typeof knowledgeRuleCommentCitationSchema
>;

export type FileCitation = z.infer<typeof fileCitationSchema>;

export const webpageCitationSchema = z.object({
  url: z.string().url(), // Webpage URL
  text: z.string().optional(), // Text of the nearest header (only used to stick it in the URL, could be omitted if we do that in Rembrandt/Christo instead of Carrie)
});

export type WebpageCitation = z.infer<typeof webpageCitationSchema>;

export const textCitationSchema = z.object({
  text: z.string(), // Text of the paragraph
});

export type TextCitation = z.infer<typeof textCitationSchema>;

// A citation from an agent that names its own source rather than a URL or a
// synthesized KDB rule: a warhol answer cites a ticket comment, a document
// page, or a chat message by id. Purely additive — every variant above keeps
// its shape, and the union is tried in order, so nothing that parsed before
// parses differently now.
export const agentSourceCitationSchema = z.object({
  source: z.string(),
  sourceId: z.string(),
  quote: z.string().optional(),
});

export type AgentSourceCitation = z.infer<typeof agentSourceCitationSchema>;

// A relationship the knowledge GRAPH found, rather than a document it matched.
//
// why its own citation type: a knowledgeRule citation answers "which rule backs
// this", and the graph's answer is "these rules relate to each other, and here
// is how". Flattened into a rule chip that is lost - the user sees the rules and
// not the conflict, the requirement, or the prior conversation that made them
// worth surfacing.
//
// Node ids are `kind:native`. `claim:`, `chat:`, `user:` and `memory:` carry the
// real row id and are safe to link. `brand:` and `requirement:` are DERIVED from
// text and have no row behind them - render them, never store them as keys.
export const graphEdgeCitationSchema = z.object({
  src: z.string(),
  dst: z.string(),
  // similar_to | requires | cited | asked_about | co_cited | mentions | scoped_to
  relation: z.string(),
  // why: the graph builder emits `weight: null` for unscored edges; a bare
  // z.coerce.number() would run Number(null) === 0 and the viz would render a
  // misleading "0.00" weight badge. Null must stay unknown (undefined).
  weight: z
    .preprocess(
      (v) => (v === null ? undefined : v),
      z.coerce.number().optional(),
    )
    .optional(),
  // Why the builder drew it, in its own words. Every edge can explain itself.
  evidence: z.string().optional(),
  // Display labels, so the viz needs no second lookup per node.
  srcLabel: z.string().optional(),
  dstLabel: z.string().optional(),
  // The Ask v2 review a `finding:` source came from, so the rail can open the
  // finding in it (`/ask-puntt/<chatId>/reviews/<reviewId>/comments/<commentId>`).
  // Absent for a v1 ticket finding.
  srcReview: z
    .object({ chatId: z.string(), reviewId: z.string(), commentId: z.string() })
    .optional(),
  // A `memory:` edge the answer uses: the sentence of the message content that
  // rests on the saved memory, exactly as written once the `[n]` markers are
  // taken out, and its index among the content's sentences. warhol writes both
  // (`agents/primary/memory_attribution.py`); the inline citation is anchored on
  // `supportText`. why declared: the parse below strips an undeclared key.
  supportText: z.string().optional(),
  supportIndex: z.number().int().nonnegative().optional(),
  // The saved memory itself, for the popover the inline citation opens: its
  // whole text (`srcLabel` is cut at 120 characters), its raw type slug, when it
  // was saved (ISO) and the raw `source` slug of what saved it.
  memoryText: z.string().optional(),
  memoryType: z.string().optional(),
  memoryCreatedAt: z.string().optional(),
  memoryOrigin: z.string().optional(),
  // The brand the memory is saved under; warhol never cites another brand's
  // note and sends it with the other provenance fields (`store/graph/serve.py`).
  memoryBrand: z.string().optional(),
});

export type GraphEdgeCitation = z.infer<typeof graphEdgeCitationSchema>;

export const graphCitationSchema = z.object({
  // The node this citation is ABOUT - usually claim:<ruleId>.
  graphNodeId: z.string(),
  graphLabel: z.string().optional(),
  // The subgraph behind the conclusion: what it connects to and how.
  edges: z.array(graphEdgeCitationSchema).default([]),
  // Set when the rules disagree, so a chip can say so without re-deriving it.
  conflictingVerdicts: z.array(z.string()).optional(),
});

export type GraphCitation = z.infer<typeof graphCitationSchema>;

// ORDER IS THE CONTRACT. `z.union` returns the FIRST member that parses, and a
// bare `z.object` STRIPS keys it does not declare -- so a member that is loose
// about what it requires silently eats the richer variants listed after it.
//
// `fileCitationSchema` stays FIRST: every url-bearing citation is parsed by it
// and the webpage arm is never reached, which is why `text` had to be added to
// it (see the note on that schema) rather than left on the webpage arm alone.
// Do not move it.
//
// What moved is `textCitationSchema`, now last of the content arms (only the
// bare `{ _id }` fallback follows it). It is `{ text: z.string() }` and nothing
// else, so parsed ahead of the rule variant a rule citation carrying any `text`
// came back as a bare `{ text }` with `ruleId`, `sourceId` and `location`
// dropped at the boundary -- no error, no log, just a citation that can no
// longer name or open its source. A permissive member must never precede a
// specific one.
//
// The FE repeats this order in `commentSourceUtils.ts`; the two must agree, or
// a citation parses as one variant and renders as another.
//
// why (behavior impact): this arm order is a WRITE contract too, not only a
// read one — the arm that wins here is the shape that gets PERSISTED, so
// moving `textCitationSchema` last also changes the stored citation shape on
// christo's comment write endpoints for every id-bearing variant. Disclose
// that on any PR that touches this order; it is not a read-only refactor.
//
// Carrie is not the only consumer: christo's POST `/tickets/:ticketId/comments`
// and POST `/ask-puntt/reviews/:reviewId/comments` both run
// `validateSchema(...)`, which replaces the request body with the parsed one
// (`req.body = parsedData.body` in
// services/christo/lib/middleware/validateSchema.ts). So a client POSTing
// `{ knowledgeDataRecordId, text }` used to persist a bare `{ text }` and now
// persists `{ knowledgeDataRecordId }`, and likewise for `{ ruleId, text }`,
// `{ graphNodeId, text }` and `{ source, sourceId, quote, text }`. That is the
// point of the reorder — the id is what makes a stored citation openable, and
// the old order dropped it — but it is a boundary change, so the write path is
// pinned by "persists the id, not a bare text, on the christo write boundary"
// in __tests__/ticket.test.ts.
//
// Two bounds on that disclosure, so a christo owner can size it without
// re-deriving the union. (1) It is a WRITE-path change only: nothing already
// stored is rewritten and no migration is implied — only what the NEXT POST
// persists changes (plus what a read of an already-stored `{ id, text }` row
// now surfaces, which is the id it always had). (2) The trailing `{ _id }`
// arm is NOT one of the variants this fixes: `{ _id, text }` matched
// `textCitationSchema` first before the move and still does after it, because
// that arm sits BELOW the text one in both orders, so it still persists a bare
// `{ text }`. Lifting `{ _id }` above `textCitationSchema` is its own decision
// with its own blast radius; it is deliberately not part of this change.
export const ticketMessageCitationSchema = z.union([
  fileCitationSchema,
  knowledgeRuleCommentCitationSchema,
  graphCitationSchema,
  knowledgeDataRecordCitationSchema,
  agentSourceCitationSchema,
  webpageCitationSchema,
  textCitationSchema,
  z.object({
    _id: z.string(),
  }),
]);

export type TicketMessageCitation = z.infer<typeof ticketMessageCitationSchema>;

export const ticketMessageSchema = z.object({
  _id: z.string().optional(),
  createdBy: z.union([z.string(), userSchema.partial()]).optional(),
  aiPersona: z
    .object({
      name: z.string(),
      avatar: z.string(),
    })
    .optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  publishedAt: z.coerce.date().optional(),
  isDeleted: z.boolean().default(false).optional(),
  isPending: z.boolean().default(false).optional(),
  isReview: z.boolean().default(false).optional(),
  votes: z.array(ticketMessageVoteSchema).optional(),
  isAI: z.boolean().default(false),
  reviewId: z.string().optional(),
  description: z.string(),
  mentions: z
    .array(z.union([z.string(), userSchema.partial()]))
    .default([])
    .optional(),
  citations: z.array(ticketMessageCitationSchema).default([]).optional(),
  docCommentId: z.string().optional(),
  resolvedBy: z.string().optional(),
  likedBy: z.string().optional(),
  dismissedBy: z.string().optional(),
});

export type TicketMessage = z.infer<typeof ticketMessageSchema>;

export const cellAnchorSchema = z.object({
  sheetIndex: z.coerce.number().int().nonnegative(),
  sheetName: z.string(),
  range: z.string().regex(/^[A-Z]+\d+(?::[A-Z]+\d+)?$/),
});

export type CellAnchor = z.infer<typeof cellAnchorSchema>;

export const commentBboxSchema = z.object({
  x: z.coerce.number(),
  y: z.coerce.number(),
  w: z.coerce.number(),
  h: z.coerce.number(),
});

export type CommentBbox = z.infer<typeof commentBboxSchema>;

export const ticketCommentSchema = ticketMessageSchema.extend({
  messages: z.array(ticketMessageSchema),
  x: z.coerce.number().optional(),
  y: z.coerce.number().optional(),
  rel_x: z.coerce.number().optional(),
  rel_y: z.coerce.number().optional(),
  page_num: z.coerce.number().optional(),
  videoStart: z.coerce.number().optional(),
  videoEnd: z.coerce.number().optional(),
  cellAnchor: cellAnchorSchema.optional(),
  isRequired: z.boolean().default(false),
  rule: z.string().optional(),
  boardType: z.nativeEnum(TicketCommentBoard).optional(),
  boardId: z.string().optional(),
  shapeId: z.string().optional(),
  disposition: z
    .nativeEnum(TicketCommentDisposition)
    .default(TicketCommentDisposition.DEFAULT),
  liked: z.boolean().nullable().optional(),
  atomic_task: z.string().optional(),
  locationDescription: z.string().optional(),
  bbox: commentBboxSchema.optional(),
  compliance: complianceVerdictMetaSchema.optional(),
});

export type TicketComment = z.infer<typeof ticketCommentSchema>;

/**
 * A human review decision on a specific document version, set by the Ask Puntt
 * agent from the chat ("approve this" / "I need changes"). Tri-state: the field
 * is absent until a human decides, `approved` when they sign off, and
 * `changes_requested` when they ask for edits. Kept in sync with the legacy
 * `approval` boolean below (`approval === (decision === "approved")`).
 */
export const reviewDecisionSchema = z.enum(["approved", "changes_requested"]);
export type ReviewDecision = z.infer<typeof reviewDecisionSchema>;

export const ticketReviewSchema = z.object({
  reviewer: z.union([z.string(), userSchema.partial()]),
  description: z.string().optional(),
  approval: z.boolean().optional().nullable(),
  // Tri-state human approval decision (approved | changes_requested). Absent
  // until a human decides. WHO = `reviewer`, WHEN = `createdAt`/`updatedAt`.
  decision: reviewDecisionSchema.optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export type TicketReview = z.infer<typeof ticketReviewSchema>;

export const boardSchema = z.object({
  _id: z.string().optional(),
  shapes: z.any(), //z.custom<TLStoreSnapshot>(),
  version: z.number().optional(), // for conflict resolution
  screenshotUrl: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  deletedAt: z.coerce.date().optional(),
});

export type Board = z.infer<typeof boardSchema>;

const reviewFilesSchema = fileCoordSchema.extend({
  height: z.coerce.number().optional(),
  width: z.coerce.number().optional(),
  x: z.coerce.number().optional(),
  y: z.coerce.number().optional(),
  missingFonts: z.array(z.string()).optional(),
});

export type ReviewFiles = z.infer<typeof reviewFilesSchema>;

export const contextFileSchema = z.object({
  context: z.string().optional(),
  source: z.string(),
});

export type ContextFile = z.infer<typeof contextFileSchema>;

export const contextAssetSchema = z.object({
  source: z.string(),
  key: z.string(), // S3 key for aicache lookup
  bucket: z.string().optional(),
  type: z.string().optional(),
  contentType: z.string().optional(),
  name: z.string().optional(),
});

export type ContextAsset = z.infer<typeof contextAssetSchema>;

export const revisionBoardSchema = boardSchema.extend({
  createdBy: z.union([z.string(), userSchema.partial()]),
  isAIReviewed: z.boolean().optional(),
  isAIReady: z.boolean().default(false).optional(),
  isRunningAIReview: z.boolean().default(false).optional(),
  aiReviewWorkflowId: z.string().nullable().optional(),
  isPending: z.boolean().default(false).optional(),
  errorDetails: z
    .object({
      reason: z.nativeEnum(RevisionBoardErrorReason).optional(),
    })
    .optional(),
  revision: z.coerce.number().default(0).optional(),
  reviews: z.array(ticketReviewSchema).optional(),
  viewedBy: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  name: z.string().optional(),
  workingFiles: z.array(assetSchema).optional(), // @deprecated
  assetContext: z.string().optional(),
  reviewFiles: z.array(reviewFilesSchema).optional(),
  contextFiles: z.array(contextFileSchema).optional(),
  downloadFile: assetSchema.optional(),
  approval: z.boolean().optional().nullable(),
  documentImportType: z.string().default(""),
  thumbnails: z.array(z.string()).default([]),
  allowList: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  aiVersion: z.number().optional(),
  timeSaved: z.number().optional(),
  timeReviewTook: z.number().optional(),
  atomic_tasks: z.array(z.string()).optional(),
  shadow: z
    .object({
      version: z.number().optional(),
      boardId: z.string().optional(),
    })
    .optional(),
  context: z
    .object({
      thread: z
        .object({
          openai: z
            .object({
              id: z.string().optional(),
            })
            .optional(),
        })
        .optional(),
    })
    .optional(),
  originalRevisionBoardId: z.string().optional(),
  isAskPunttReady: z.boolean().optional(),
  videoChunkKeys: z.array(z.string()).optional(),
  videoGcsUris: z.array(z.string()).optional(),
});

export type RevisionBoard = z.infer<typeof revisionBoardSchema>;

export const briefFileSchema = z.object({
  ticketName: z.string(),
  images: z.array(z.string()).default([]),
});

export type BriefFile = z.infer<typeof briefFileSchema>;

export const redlinesSchema = z.object({
  _id: z.string(),
  type: z.string(),
  authorId: z.string(),
  createdAt: z.string().datetime(),
  hasComments: z.boolean().optional(),
  data: z.object({}).optional().nullable(), // we might not need either of these
  attributes: z.object({}).optional().nullable(), // we might not need either of these
  commentThreadId: z.string().optional(),
});

export type Redlines = z.infer<typeof redlinesSchema>;
export const ticketSchema = z.object({
  _id: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  updatedIndex: z.string().optional(),
  folder: z.union([z.string(), folderSchema]).optional(),
  status: z.nativeEnum(TicketStatus).default(TicketStatus.WIP).optional(),
  createdBy: z.union([z.string(), userSchema.partial()]),
  enterprise: z.union([z.string(), enterpriseSchema]),
  title: z.string(),
  description: z.string().optional(),
  files: z.array(assetSchema).optional(),
  workflow: workflowSchema,
  workflowStep: z.number().default(0),
  briefHistory: z.array(briefHistorySchema).optional(),
  briefBoard: boardSchema.optional(),
  briefFiles: z.array(briefFileSchema).optional(),
  moodBoard: boardSchema.optional(),
  revisionBoards: z.array(revisionBoardSchema).optional(),
  reviewers: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  comments: z.array(ticketCommentSchema).optional(),
  redlines: z.array(redlinesSchema).optional(),
  asset_context: z.record(z.any()).optional(),
  participants: z
    .array(z.union([z.string(), userSchema.partial()]))
    .default([]),
  allowList: z.array(z.union([z.string(), userSchema.partial()])).optional(),
  creatives: z
    .array(z.union([z.string(), userSchema.partial()]))
    .default([])
    .optional(),
  archivedAt: z.coerce.date().optional(),
  team: z.union([z.string(), teamSchema]).optional(),
  suggestions: z.array(z.string()).optional(),
  markup: z.string().optional(),
  ai_context: z
    .object({
      brand_name: z.string().optional(),
      document_type_classification: z.string().optional(),
      context_instructions: z.string().optional(),
      analysis_timestamp: z.string().optional(),
      document_summary: z.string().optional(),
    })
    .optional(),
  rating: z.number().min(1).max(10).nullable().default(null).optional(),
  aiStatus: z
    .nativeEnum(TicketAIStatus)
    .default(TicketAIStatus.CREATED)
    .optional(),
  contextAssets: z.array(contextAssetSchema).optional(),
  isHidden: z.boolean().optional(),
  idempotencyKey: z.string().optional(),
});

export type Ticket = z.infer<typeof ticketSchema>;

export const ticketTemplateSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  workflow: z.nativeEnum(TicketWorkflow).optional(),
});

export type TicketTemplate = z.infer<typeof ticketTemplateSchema>;

export const ticketParticipantsSchema = z
  .array(
    z.object({
      _id: z.string(),
      // Tolerate User docs missing `name` (legacy seeded users, requesters
      // created without a profile, deleted users still referenced by tickets).
      // Falling back to a placeholder keeps the whole /tickets page from
      // crashing when one participant is malformed. `.default()` (rather than
      // `.optional().transform()`) keeps the inferred output type as
      // `string` instead of `string | undefined`.
      name: z.string().default("Unknown user"),
      avatar: z.string().optional(),
      // Persisted placeholder avatar colors so a participant's chosen colors
      // render wherever others see their initials avatar. Populated by christo
      // via AVATAR_USER_POPULATE_FIELDS; absent for legacy users (FE falls back
      // to the deterministic default keyed on the participant id).
      avatarBackgroundColor: z.string().optional(),
      avatarTextColor: z.string().optional(),
      // Server-side flag set when the participant has a CATALYST_AI profile
      // for this enterprise. UI should prefer this over fragile name-string
      // matching: the AI user's `name` is "AI Reviewer" for migration-seeded
      // enterprises (enverus/mg/kenvue, see migration 0068) but can be
      // anything for enterprises where MG admins manually assigned the
      // CATALYST_AI role to an existing user.
      isAI: z.boolean().optional(),
    }),
  )
  .default([]);

export type TicketParticipants = z.infer<typeof ticketParticipantsSchema>;
